import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  auth, 
  onAuthStateChanged, 
  db, 
  doc, 
  getDoc, 
  setDoc, 
  Timestamp, 
  FirebaseUser,
  signOut,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  collection,
  query,
  limit,
  onSnapshot,
  checkInvoiceDueDates,
  checkTaskDueDates,
  checkContractExpirations,
  updateDoc,
  where,
  getDocs
} from './firebase';
import { UserProfile, UserRole } from './types';
import { USER_ROLES } from './constants';
import { ConnectionStatus } from './components/ConnectionStatus';
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  ClipboardList, 
  FileText, 
  LogOut, 
  Menu, 
  X, 
  ChevronRight,
  ArrowLeft,
  User as UserIcon,
  ShieldCheck,
  Zap,
  Eye,
  EyeOff,
  Building2,
  Wrench,
  FileBarChart,
  MessageSquare,
  Phone,
  MessageCircle,
  ExternalLink as ExternalLinkIcon,
  Copyright,
  Receipt,
  Wallet,
  DollarSign,
  Bell,
  Search,
  Wifi,
  WifiOff,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Truck,
  ConciergeBell,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';
import { ChatWidget } from './components/ChatWidget';

// --- Utils ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Auth Context ---
interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isTechnician: boolean;
  isTechnicalDept: boolean;
  isBilling: boolean;
  isAccountant: boolean;
  isCashier: boolean;
  isHR: boolean;
  isMD: boolean;
  isStore: boolean;
  isCustomerCare: boolean;
  isOpsManager: boolean;
  isLogistics: boolean;
  isReceptionist: boolean;
  isHOD: boolean;
  isAccountantHOD: boolean;
  isBillingHOD: boolean;
  isTechnicalHOD: boolean;
  isOperationsHOD: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (firebaseUser) {
        // Proactively restore admin privileges for jcksnmandela@gmail.com if they are the logged in user or the main administrator
        const isSelfAdmin = firebaseUser.email === 'ramitsolution.business@gmail.com' || firebaseUser.email === 'jcksnmandela@gmail.com';
        if (isSelfAdmin) {
          (async () => {
            try {
              const q = query(collection(db, 'users'), where('email', '==', 'jcksnmandela@gmail.com'));
              const querySnapshot = await getDocs(q);
              if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];
                const userData = userDoc.data();
                if (userData.role !== 'admin' || userData.isActive !== true) {
                  await updateDoc(doc(db, 'users', userDoc.id), { role: 'admin', isActive: true });
                  console.log('Proactively restored admin privileges for jcksnmandela@gmail.com in Firestore.');
                }
              }
            } catch (err) {
              console.warn('Error proactively restoring admin role:', err);
            }
          })();
        }

        unsubscribeProfile = onSnapshot(doc(db, 'users', firebaseUser.uid), async (snapshot) => {
          if (snapshot.exists()) {
            const profileData = snapshot.data() as UserProfile;
            const isProtectedEmail = firebaseUser.email === 'ramitsolution.business@gmail.com' || firebaseUser.email === 'jcksnmandela@gmail.com';
            
            if (profileData.isActive === false && !isProtectedEmail) {
              await signOut(auth);
              setUser(null);
              setProfile(null);
              setLoading(false);
              return;
            }
            
            // Auto-heal role for protected administrators if somehow changed
            if (isProtectedEmail && (profileData.role !== USER_ROLES.ADMIN || profileData.isActive !== true)) {
              const updatedProfile = { ...profileData, role: USER_ROLES.ADMIN, isActive: true };
              await updateDoc(doc(db, 'users', firebaseUser.uid), { role: USER_ROLES.ADMIN, isActive: true });
              setProfile(updatedProfile);
            } else {
              setProfile(profileData);
            }
            
            checkInvoiceDueDates();
            checkTaskDueDates();
            if (profileData.role === USER_ROLES.ADMIN || profileData.role === USER_ROLES.MANAGER) {
              checkContractExpirations();
            }
            setLoading(false);
          } else {
            // Create default profile for new users
            const isDefaultAdmin = firebaseUser.email === 'ramitsolution.business@gmail.com' || firebaseUser.email === 'jcksnmandela@gmail.com';
            const newProfile: UserProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Admin',
              role: isDefaultAdmin ? USER_ROLES.ADMIN : USER_ROLES.TECHNICIAN,
              isActive: true,
              createdAt: Timestamp.now(),
            };
            await setDoc(doc(db, 'users', firebaseUser.uid), newProfile);
            setProfile(newProfile);
            setLoading(false);
          }
        }, (error) => {
          console.error("Error listening to profile:", error);
          setLoading(false);
        });
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const userRef = doc(db, 'users', user.uid);
    updateDoc(userRef, { isOnline: true, lastActive: new Date().toISOString() })
      .catch(err => console.warn("Error setting online:", err));

    const handleBeforeUnload = () => {
      updateDoc(userRef, { isOnline: false, lastActive: new Date().toISOString() })
        .catch(err => console.warn("Error setting offline:", err));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    const interval = setInterval(() => {
      updateDoc(userRef, { isOnline: true, lastActive: new Date().toISOString() })
        .catch(err => console.warn("Heartbeat fail:", err));
    }, 45000);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(interval);
      updateDoc(userRef, { isOnline: false, lastActive: new Date().toISOString() })
        .catch(err => console.warn("Error setting offline:", err));
    };
  }, [user]);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Sign in with email error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAdmin = !!(profile?.role === USER_ROLES.ADMIN || profile?.email === 'jcksnmandela@gmail.com' || profile?.email === 'ramitsolution.business@gmail.com');
  const isManager = !!(profile?.role === USER_ROLES.MANAGER || isAdmin);
  const isTechnician = !!(profile?.role === USER_ROLES.TECHNICIAN || isManager);
  const isTechnicalDept = !!(profile?.role === USER_ROLES.TECHNICAL || isManager || (profile?.isHOD && profile?.hodDepartments?.includes('Technical')));
  const isBilling = !!(profile?.role === USER_ROLES.BILLING || isManager || (profile?.isHOD && profile?.hodDepartments?.includes('Billing')));
  const isBillingHOD = !!(profile?.isHOD && profile?.hodDepartments?.includes('Billing'));
  const isAccountant = !!(profile?.role === USER_ROLES.ACCOUNTANT || isManager || (profile?.isHOD && profile?.hodDepartments?.includes('Finance')));
  const isCashier = !!(profile?.role === USER_ROLES.CASHIER || isManager || (profile?.isHOD && profile?.hodDepartments?.includes('Finance')));
  const isHR = !!(profile?.role === USER_ROLES.HR || isAdmin);
  const isMD = !!(profile?.role === USER_ROLES.MD || isAdmin);
  const isStore = !!(profile?.role === USER_ROLES.STORE || isManager);
  const isCustomerCare = !!(profile?.role === USER_ROLES.CUSTOMER_CARE || isManager);
  const isLogistics = !!(profile?.role === USER_ROLES.LOGISTICS || isManager);
  const isReceptionist = !!(profile?.role === USER_ROLES.RECEPTIONIST || isManager);
  const isOpsManager = !!(profile?.role === USER_ROLES.OPS_MANAGER || isManager || (profile?.isHOD && profile?.hodDepartments?.includes('Operations')));
  const isTechnicalHOD = !!(profile?.isHOD && profile?.hodDepartments?.includes('Technical'));
  const isAccountantHOD = !!(profile?.isHOD && profile?.hodDepartments?.includes('Finance'));
  const isOperationsHOD = !!(profile?.isHOD && profile?.hodDepartments?.includes('Operations'));
  const isHOD = !!(profile?.isHOD || isAdmin);

  return (
    <AuthContext.Provider value={{ 
      user, profile, loading, 
      isAdmin, isManager, isTechnician, isTechnicalDept, isBilling, isAccountant, isCashier, isHR, isMD, isStore, isCustomerCare, isOpsManager, isHOD,
      isLogistics, isReceptionist,
      isTechnicalHOD, isBillingHOD, isAccountantHOD, isOperationsHOD,
      signInWithEmail, logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// --- Components ---

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: UserRole }> = ({ children, requiredRole }) => {
  const { user, profile, loading, isAdmin, isManager, isTechnician, isTechnicalDept, isBilling, isAccountant, isCashier, isHR, isMD, isStore, isCustomerCare, isOpsManager, isHOD, isLogistics, isReceptionist } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div className="min-h-screen bg-blue-900 flex items-center justify-center text-white">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="font-bold text-sm tracking-wide text-blue-200">Loading Bright Technical ERP...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (requiredRole) {
    let hasAccess = false;
    if (isAdmin) hasAccess = true;
    else if (requiredRole === USER_ROLES.ADMIN) hasAccess = isAdmin;
    else if (requiredRole === USER_ROLES.MANAGER) hasAccess = isManager;
    else if (requiredRole === USER_ROLES.TECHNICIAN) hasAccess = isTechnician;
    else if (requiredRole === USER_ROLES.TECHNICAL) hasAccess = isTechnicalDept;
    else if (requiredRole === USER_ROLES.BILLING) hasAccess = isBilling;
    else if (requiredRole === USER_ROLES.ACCOUNTANT) hasAccess = isAccountant;
    else if (requiredRole === USER_ROLES.CASHIER) hasAccess = isCashier;
    else if (requiredRole === USER_ROLES.HR) hasAccess = isHR || isHOD;
    else if (requiredRole === USER_ROLES.MD) hasAccess = isMD;
    else if (requiredRole === USER_ROLES.OPS_MANAGER) hasAccess = isOpsManager;
    else if (requiredRole === USER_ROLES.STORE) hasAccess = isStore;
    else if (requiredRole === USER_ROLES.CUSTOMER_CARE) hasAccess = isCustomerCare;
    else if (requiredRole === USER_ROLES.LOGISTICS) hasAccess = isLogistics;
    else if (requiredRole === USER_ROLES.RECEPTIONIST) hasAccess = isReceptionist;
    else if (requiredRole === USER_ROLES.PROCUREMENT) hasAccess = profile?.role === USER_ROLES.PROCUREMENT || isStore || isManager;
    
    if (!hasAccess) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const Sidebar = ({ isOpen, toggle, logoUrl }: { isOpen: boolean; toggle: () => void; logoUrl: string | null }) => {
  const { profile, logout, isAdmin, isManager, isTechnicalDept, isTechnician, isAccountant, isBilling, isCashier, isHR, isMD, isStore, isCustomerCare, isOpsManager, isHOD, isTechnicalHOD } = useAuth();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [pendingInvoicesCount, setPendingInvoicesCount] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('pending_invoice_drafts');
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed.length : 0;
      }
    } catch (e) {
      console.error(e);
    }
    return 0;
  });

  useEffect(() => {
    const updateCount = () => {
      try {
        const saved = localStorage.getItem('pending_invoice_drafts');
        const nextCount = saved ? (Array.isArray(JSON.parse(saved)) ? JSON.parse(saved).length : 0) : 0;
        setTimeout(() => {
          setPendingInvoicesCount(nextCount);
        }, 0);
      } catch (e) {
        console.error(e);
        setTimeout(() => {
          setPendingInvoicesCount(0);
        }, 0);
      }
    };

    window.addEventListener('storage', updateCount);
    window.addEventListener('pending_drafts_updated', updateCount);
    const interval = setInterval(updateCount, 2000);

    return () => {
      window.removeEventListener('storage', updateCount);
      window.removeEventListener('pending_drafts_updated', updateCount);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const role = profile?.role;
  const isProcurementRole = role === USER_ROLES.PROCUREMENT;

  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({
    'QA&SPE': false
  });

  const toggleSubmenu = (name: string) => {
    setOpenSubmenus(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const navItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, show: true },
    { name: 'HR Dashboard', path: '/hr-dashboard', icon: Users, show: isAdmin || isHR || isHOD },
    { name: 'Accounts Dashboard', path: '/accounts-dashboard', icon: Wallet, show: isAdmin || isManager || isAccountant },
    { name: 'Billing Dashboard', path: '/billing-dashboard', icon: Receipt, show: isAdmin || isManager || isBilling },
    { name: 'Cashier Dashboard', path: '/cashier', icon: Wallet, show: isAdmin || isManager || isCashier || isAccountant },
    { name: 'Store Dashboard', path: '/store-dashboard', icon: ClipboardList, show: isAdmin || isManager || isStore || isProcurementRole },
    { name: 'Customer Care', path: '/customer-care', icon: MessageSquare, show: isAdmin || isManager || isCustomerCare },
    { name: 'Transportation', path: '/transportation', icon: Truck, show: isAdmin || isManager || role === USER_ROLES.LOGISTICS },
    { name: 'Receptionist', path: '/receptionist', icon: ConciergeBell, show: isAdmin || isManager || role === USER_ROLES.RECEPTIONIST },
    { name: 'Technician Dashboard', path: '/technician-dashboard', icon: ClipboardList, show: isAdmin || isManager || isTechnician },
    { name: 'Technical Dept', path: '/technical', icon: ClipboardList, show: isAdmin || isManager || isTechnicalDept },
    { name: 'Procurement', path: '/procurement', icon: ShoppingBag, show: isAdmin || isManager || isStore || isProcurementRole },
    
    // Core Modules
    { name: 'Users', path: '/users', icon: Users, show: isAdmin },
    { name: 'Users & Offices', path: '/users-management', icon: Users, show: isAdmin },
    { name: 'Clients', path: '/clients', icon: Building2, show: isAdmin || isManager || isBilling || isCustomerCare },
    { name: 'Service Schedules', path: '/service-schedules', icon: Calendar, show: isAdmin || isManager || isCustomerCare || isTechnicalDept || isBilling || isMD || isOpsManager || isTechnician },
    { name: 'Service Types', path: '/service-types', icon: ClipboardList, show: isAdmin || isManager },
    { name: 'Tasks', path: '/tasks', icon: Wrench, show: role !== USER_ROLES.RECEPTIONIST },
    { name: 'Reports', path: '/reports', icon: FileBarChart, show: role !== USER_ROLES.RECEPTIONIST },
    { name: 'Devices & Assets', path: '/devices', icon: Zap, show: isAdmin || isManager || isStore || isTechnician || isTechnicalDept },
    { name: 'Invoices', path: '/invoices', icon: Receipt, show: false },
    { name: 'Accounts & Expenses', path: '/accounts', icon: Wallet, show: isAdmin || isManager || isAccountant || isCashier },
    { 
      name: 'QA&SPE', 
      icon: ShieldCheck, 
      show: isAdmin || isManager || isTechnicalHOD || isMD || isOpsManager || isCustomerCare,
      subItems: [
        { name: 'Service Performance Evaluation', path: '/qa-spe?tab=evaluate' },
        { name: 'Quality Assurance Category', path: '/qa-spe?tab=category' }
      ]
    },
    { name: 'My Payroll', path: '/my-payroll', icon: DollarSign, show: true },
    { name: 'Feedback', path: '/feedback', icon: MessageSquare, show: true },
    { name: 'My Profile', path: '/profile', icon: UserIcon, show: true },
    { name: 'Alert Settings', path: '/user-settings', icon: Bell, show: true },
    { name: 'Settings', path: '/settings', icon: ShieldCheck, show: isAdmin },
  ];

  const filteredNavItems = navItems.filter(
    item => item.show && (
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.subItems && item.subItems.some(sub => sub.name.toLowerCase().includes(searchQuery.toLowerCase())))
    )
  );

  const whatsappUrl = "https://wa.me/255689460993";

  return (
    <>
      {/* Mobile Backdrop */}
      <AnimatePresence>
        {isOpen && !isDesktop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggle}
            className="fixed inset-0 z-[50] bg-blue-900/40 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar Content */}
      <motion.aside
        initial={false}
        animate={{ x: isOpen ? 0 : -280 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 bottom-0 left-0 z-[60] w-[280px] bg-blue-900 text-white shadow-2xl lg:shadow-[20px_0_40px_-20px_rgba(30,58,138,0.3)] overflow-hidden"
      >
        <div className="flex flex-col h-full">
          {/* Logo & Close */}
          <div className="p-8 pb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20 overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <Zap className="text-white w-7 h-7" />
                )}
              </div>
              <div>
                <h1 className="font-black text-white text-xl tracking-tight">BTSGS</h1>
                <p className="text-[10px] text-blue-200 font-bold uppercase tracking-widest">Management</p>
              </div>
            </div>
            <button onClick={toggle} className="lg:hidden p-2.5 hover:bg-white/10 text-white rounded-xl transition-colors cursor-pointer flex items-center justify-center">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Menu Filter Input */}
          <div className="px-6 pb-4">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
              <input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2 bg-white/5 border border-white/10 hover:border-white/20 focus:border-orange-500/50 rounded-2xl text-xs font-bold text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-blue-300 hover:text-white transition-colors p-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 px-4 py-2 space-y-1.5 overflow-y-auto">
            {filteredNavItems.length === 0 ? (
              <div className="px-5 py-8 text-center text-blue-300/60 italic text-xs font-semibold">
                No matching menus
              </div>
            ) : (
              filteredNavItems.map((item) => {
                if (item.subItems) {
                  const isSubOpen = !!openSubmenus[item.name];
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        onClick={() => toggleSubmenu(item.name)}
                        className={cn(
                          "flex items-center gap-3 w-full px-5 py-3.5 rounded-2xl transition-all group relative overflow-hidden cursor-pointer text-blue-100 hover:bg-white/5 hover:text-white focus-visible:outline-none"
                        )}
                      >
                        <item.icon className="w-5 h-5 text-blue-300 group-hover:text-white" />
                        <span className="font-bold tracking-tight text-left flex-1">{item.name}</span>
                        {isSubOpen ? <ChevronUp className="w-4 h-4 text-blue-300" /> : <ChevronDown className="w-4 h-4 text-blue-300" />}
                      </button>
                      <AnimatePresence>
                        {isSubOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-6 space-y-1 overflow-hidden"
                          >
                            {item.subItems.map(sub => (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                onClick={() => window.innerWidth < 1024 && toggle()}
                                className={cn(
                                  "flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer",
                                  location.pathname + location.search === sub.path || (location.pathname === sub.path.split('?')[0] && location.search.includes(sub.path.split('?')[1]))
                                    ? "bg-white/10 text-white font-black"
                                    : "text-blue-200 hover:bg-white/5 hover:text-white"
                                )}
                              >
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span>{sub.name}</span>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path!}
                    onClick={() => window.innerWidth < 1024 && toggle()}
                    className={cn(
                      "flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all group relative overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900 focus-visible:bg-white/10",
                      location.pathname === item.path
                        ? "bg-orange-500 text-white shadow-xl shadow-orange-500/30"
                        : "text-blue-100 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <item.icon className={cn("w-5 h-5 transition-colors", location.pathname === item.path ? "text-white" : "text-blue-300 group-hover:text-white")} />
                    <span className="font-bold tracking-tight">{item.name}</span>
                    {item.name === 'Invoices' && pendingInvoicesCount > 0 && (
                      <span className={cn(
                        "ml-auto text-[10px] font-black px-2 py-0.5 rounded-full transition-all shrink-0 animate-pulse",
                        location.pathname === item.path
                          ? "bg-white text-orange-600"
                          : "bg-orange-500 text-white"
                      )}>
                        {pendingInvoicesCount}
                      </span>
                    )}
                    {location.pathname === item.path && (
                      <motion.div layoutId="activeNav" className={cn(item.name === 'Invoices' && pendingInvoicesCount > 0 ? "ml-1.5" : "ml-auto")}>
                        <ChevronRight className="w-4 h-4" />
                      </motion.div>
                    )}
                  </Link>
                );
              })
            )}
          </nav>

          {/* Footer controls & Connection Status */}
          <div className="p-6 space-y-3.5 shrink-0 border-t border-white/5 bg-blue-950/20">
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 w-full px-5 py-3 bg-emerald-500 hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900 text-white rounded-2xl transition-all shadow-lg shadow-emerald-500/20 group"
            >
              <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <div className="text-left">
                <p className="text-[9px] font-bold uppercase tracking-widest opacity-85">Contact Support</p>
                <p className="text-xs font-black">WhatsApp Assist</p>
              </div>
            </a>

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/10">
              <div className="w-9 h-9 bg-blue-800 rounded-full flex items-center justify-center border border-white/10 overflow-hidden shrink-0">
                {profile?.photoURL ? (
                  <img src={profile.photoURL} alt="" className="w-full h-full object-cover" />
                ) : profile?.displayName ? (
                  <img src={`https://ui-avatars.com/api/?name=${profile.displayName}&background=random`} alt="" />
                ) : (
                  <UserIcon className="w-5 h-5 text-blue-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-white truncate leading-tight">{profile?.displayName || 'User'}</p>
                <p className="text-[9px] text-blue-300 font-bold uppercase tracking-widest truncate leading-tight mt-0.5">{profile?.role}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 xl:gap-4">
              <button
                onClick={logout}
                className="flex items-center gap-2.5 px-3 py-2 text-blue-200 hover:bg-red-500/10 hover:text-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:bg-red-500/10 rounded-xl transition-all font-bold text-xs cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>

              {/* Online Offline secure connectivity badge */}
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/5 text-[10px] font-bold uppercase tracking-widest">
                {isOnline ? (
                  <>
                    <Wifi className="w-3.5 h-3.5 text-emerald-400 animate-pulse shrink-0" />
                    <span className="text-emerald-400/90 text-[9px]">Online</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3.5 h-3.5 text-red-400 animate-bounce shrink-0" />
                    <span className="text-red-400 text-[9px]">Offline</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

const Footer = () => {
  return (
    <footer className="bg-blue-900 border-t border-blue-800 py-10 px-6 lg:px-10 mt-auto text-white">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex flex-col items-center md:items-start gap-2">
          <div className="flex items-center gap-2">
            <Zap className="text-white w-5 h-5" />
            <span className="font-black text-white tracking-tight">BTSGS</span>
          </div>
          <p className="text-xs text-blue-200 font-medium">© {new Date().getFullYear()} Bright Technical Systems and General Supplies Limited.</p>
        </div>
        
        <div className="flex flex-col items-center md:items-end gap-2">
          <p className="text-xs font-bold text-white">Designed by <span className="text-orange-400">Jackson Mandela</span></p>
          <div className="flex items-center gap-4">
            <a href="https://wa.me/255689460993" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 transition-colors">
              <MessageCircle className="w-5 h-5" />
            </a>
            <a href="#" className="text-white hover:text-blue-200 transition-colors">
              <ExternalLinkIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const { profile } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>('/Logo.png');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1024 && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    const q = query(collection(db, 'settings'), limit(1));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const settings = snapshot.docs[0].data() as any;
        const url = settings.logoUrl || '/Logo.png';
        setLogoUrl(url);
        
        // Update favicon
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = url;
      }
    }, (error) => {
      console.error('Layout settings listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col overflow-x-hidden">
      <Sidebar isOpen={isSidebarOpen} toggle={() => setIsSidebarOpen(!isSidebarOpen)} logoUrl={logoUrl} />
      
      <div className={cn("transition-all duration-300 ease-in-out flex-1 flex flex-col", isSidebarOpen ? "lg:ml-[280px]" : "lg:ml-0")}>
        <header className="sticky top-0 z-30 bg-orange-500 backdrop-blur-md border-b border-orange-600 px-4 py-3.5 sm:px-6 sm:py-5 flex items-center justify-between text-white shadow-lg shadow-orange-500/20">
          <div className="flex items-center gap-3 sm:gap-4">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
              className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all active:scale-95 cursor-pointer flex items-center justify-center"
              title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
            >
              {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="flex items-center gap-2.5 sm:gap-3 lg:hidden shrink-0">
              {location.pathname !== '/' && (
                <button onClick={() => navigate(-1)} className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer flex items-center justify-center">
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <Zap className="text-orange-500 w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </div>
              <h1 className="font-black text-white text-lg sm:text-xl tracking-tight">BTSGS</h1>
            </div>
            
            <div className="hidden lg:flex items-center gap-3">
              {location.pathname !== '/' && (
                <button onClick={() => navigate(-1)} className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer flex items-center justify-center">
                  <ArrowLeft className="w-6 h-6" />
                </button>
              )}
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <Zap className="text-orange-500 w-6 h-6" />
                )}
              </div>
              <div>
                <h2 className="text-base font-black text-white tracking-tight">Bright Technical Systems</h2>
                <p className="text-[10px] text-blue-900 uppercase tracking-[0.2em] font-black">General Supplies Limited</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
            <ConnectionStatus />
            <ContractReminders />
            <NotificationCenter />
          </div>
        </header>

        <ContractCountdownBanner />

        <main className="p-4 sm:p-6 lg:p-10 w-full flex-1">
          {children}
        </main>

        <Footer />
        {profile && <ChatWidget />}
      </div>
    </div>
  );
};

// --- Pages ---

const Login = () => {
  const { signInWithEmail, user, loading } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>('/Logo.png');

  useEffect(() => {
    const q = query(collection(db, 'settings'), limit(1));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const settings = snapshot.docs[0].data() as any;
        setLogoUrl(settings.logoUrl || '/Logo.png');
      }
    }, (error) => {
      console.error('Login settings listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-blue-900 flex items-center justify-center text-white">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="font-bold text-sm tracking-wide text-blue-200">Loading Bright Technical ERP...</p>
      </div>
    </div>
  );
  if (user) return <Navigate to={from} replace />;

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError('Invalid email or password.');
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setIsResetting(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success('Password reset email sent! Please check your inbox.');
      setIsResetModalOpen(false);
      setResetEmail('');
    } catch (err: any) {
      toast.error('Error: ' + err.message);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-900 p-6 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-orange-500/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/20 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-[40px] shadow-2xl shadow-blue-950/50 p-12 text-center relative z-10"
      >
        <div className="w-24 h-24 bg-blue-900 rounded-[32px] flex items-center justify-center mx-auto mb-10 shadow-xl shadow-blue-900/20 overflow-hidden">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
          ) : (
            <Zap className="text-white w-12 h-12" />
          )}
        </div>
        
        <div className="mb-10">
          <h1 className="text-4xl font-black text-blue-900 mb-3 tracking-tight">BTSGS</h1>
          <p className="text-slate-500 font-medium">Bright Technical Systems & General Supplies Ltd</p>
        </div>
        
        <form onSubmit={handleEmailLogin} className="space-y-4 mb-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-900"
          />
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-900 pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-900 text-white py-4 rounded-2xl font-bold hover:bg-blue-800 transition-all disabled:opacity-50"
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <button 
          onClick={() => setIsResetModalOpen(true)}
          className="text-sm font-bold text-blue-800 hover:text-blue-900 transition-colors cursor-pointer"
        >
          Forgot Password?
        </button>

        <div className="mt-12 pt-8 border-t border-slate-50">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">
            Secure Management Portal
          </p>
        </div>
      </motion.div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {isResetModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsResetModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[2.5rem] p-8 w-full max-w-sm relative z-10 shadow-2xl"
            >
              <h3 className="text-xl font-black text-blue-900 mb-2">Reset Password</h3>
              <p className="text-sm text-slate-500 mb-6 font-medium">Enter your registered email and we'll send you a link to reset your password.</p>
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <input
                  type="email"
                  placeholder="Email Address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-900"
                />
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsResetModalOpen(false)}
                    className="flex-1 px-4 py-3 bg-slate-100 text-slate-900 rounded-2xl font-bold hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isResetting}
                    className="flex-1 px-4 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-colors disabled:opacity-50"
                  >
                    {isResetting ? 'Sending...' : 'Send Link'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

import Dashboard from './pages/Dashboard';
import UsersPage from './pages/Users';
import ClientsPage from './pages/Clients';
import TasksPage from './pages/Tasks';
import ReportsPage from './pages/Reports';
import FeedbackPage from './pages/Feedback';
import SettingsPage from './pages/Settings';
import InvoicesPage from './pages/Invoices';
import AccountsPage from './pages/Accounts';
import AccountsDashboard from './pages/AccountsDashboard';
import HRDashboard from './pages/HRDashboard';
import MyPayroll from './pages/MyPayroll';
import ProfilePage from './pages/Profile';
import UserSettingsPage from './pages/UserSettings';
import BillingDashboard from './pages/BillingDashboard';
import UserManagement from './pages/UserManagement';
import CashierDashboard from './pages/CashierDashboard';
import StoreDashboard from './pages/StoreDashboard';
import CustomerCareDashboard from './pages/CustomerCareDashboard';
import TransportationDashboard from './pages/TransportationDashboard';
import ReceptionistDashboard from './pages/ReceptionistDashboard';
import TechnicianDashboard from './pages/TechnicianDashboard';
import DevicesPage from './pages/Devices';
import TechnicalDeptPage from './pages/Technical';
import InvoiceDesigner from './pages/InvoiceDesigner';
import QASPEPage from './pages/QASPE';
import ProcurementPage from './pages/Procurement';
import ServiceTypesPage from './pages/ServiceTypes';
import ServiceSchedulesPage from './pages/ServiceSchedulesPage';
import NotificationCenter from './components/NotificationCenter';
import { ContractReminders, ContractCountdownBanner } from './components/ContractReminders';
import CashRequisitionModal from './components/CashRequisitionModal';
import { FirestoreSyncManager } from './components/FirestoreSyncManager';
import { QuotaWarningBanner } from './components/QuotaWarningBanner';

// --- Main App ---

const Home = () => {
  const { profile, isAdmin, isManager } = useAuth();
  
  if (!profile) return <Dashboard />;

  // Admin, Manager, MD, and Operations Manager land on the central Executive Dashboard
  if (isAdmin || profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.MANAGER || profile.role === USER_ROLES.MD || profile.role === USER_ROLES.OPS_MANAGER) {
    return <Dashboard />;
  }

  // Redirect each specific user role to their separate dedicated Dashboard:
  switch (profile.role) {
    case USER_ROLES.HR:
      return <Navigate to="/hr-dashboard" replace />;
    case USER_ROLES.ACCOUNTANT:
      return <Navigate to="/accounts-dashboard" replace />;
    case USER_ROLES.BILLING:
      return <Navigate to="/billing-dashboard" replace />;
    case USER_ROLES.CASHIER:
      return <Navigate to="/cashier" replace />;
    case USER_ROLES.STORE:
      return <Navigate to="/store-dashboard" replace />;
    case USER_ROLES.CUSTOMER_CARE:
      return <Navigate to="/customer-care" replace />;
    case USER_ROLES.LOGISTICS:
      return <Navigate to="/transportation" replace />;
    case USER_ROLES.RECEPTIONIST:
      return <Navigate to="/receptionist" replace />;
    case USER_ROLES.TECHNICIAN:
      return <Navigate to="/technician-dashboard" replace />;
    case USER_ROLES.TECHNICAL:
      return <Navigate to="/technical" replace />;
    case USER_ROLES.PROCUREMENT:
      return <Navigate to="/procurement" replace />;
    default:
      return <Dashboard />;
  }
};

const App = () => {
  const [isCashModalOpen, setIsCashModalOpen] = useState(false);

  useEffect(() => {
    const handleOpenRequisition = () => setIsCashModalOpen(true);
    window.addEventListener('openCashRequisition', handleOpenRequisition);
    return () => window.removeEventListener('openCashRequisition', handleOpenRequisition);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="relative">
          <button
            onClick={() => setIsCashModalOpen(true)}
            className="fixed bottom-8 right-8 z-50 w-16 h-16 bg-emerald-500 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-emerald-600 transition-all hover:scale-110 active:scale-95 group"
            title="Cash Requisition"
          >
            <DollarSign className="w-8 h-8 group-hover:rotate-12 transition-transform" />
          </button>

          <CashRequisitionModal isOpen={isCashModalOpen} onClose={() => setIsCashModalOpen(false)} />
          <FirestoreSyncManager />
          <QuotaWarningBanner />

          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/users" element={<ProtectedRoute requiredRole={USER_ROLES.ADMIN}><UsersPage /></ProtectedRoute>} />
                      <Route path="/clients" element={<ProtectedRoute requiredRole={USER_ROLES.MANAGER}><ClientsPage /></ProtectedRoute>} />
                      <Route path="/service-schedules" element={<ProtectedRoute><ServiceSchedulesPage /></ProtectedRoute>} />
                      <Route path="/service-types" element={<ProtectedRoute><ServiceTypesPage /></ProtectedRoute>} />
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/technician-dashboard" element={<ProtectedRoute requiredRole={USER_ROLES.TECHNICIAN}><TechnicianDashboard /></ProtectedRoute>} />
                      <Route path="/technical" element={<ProtectedRoute requiredRole={USER_ROLES.TECHNICIAN}><TechnicalDeptPage /></ProtectedRoute>} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/qa-spe" element={<ProtectedRoute><QASPEPage /></ProtectedRoute>} />
                      <Route path="/procurement" element={<ProtectedRoute requiredRole={USER_ROLES.PROCUREMENT}><ProcurementPage /></ProtectedRoute>} />
                      <Route path="/devices" element={<DevicesPage />} />
                      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
                      <Route path="/invoice-designer" element={<ProtectedRoute><InvoiceDesigner /></ProtectedRoute>} />
                      <Route path="/accounts" element={<ProtectedRoute requiredRole={USER_ROLES.ACCOUNTANT}><AccountsPage /></ProtectedRoute>} />
                      <Route path="/accounts-dashboard" element={<ProtectedRoute requiredRole={USER_ROLES.ACCOUNTANT}><AccountsDashboard /></ProtectedRoute>} />
                      <Route path="/hr-dashboard" element={<ProtectedRoute requiredRole={USER_ROLES.HR}><HRDashboard /></ProtectedRoute>} />
                      <Route path="/my-payroll" element={<ProtectedRoute><MyPayroll /></ProtectedRoute>} />
                      <Route path="/billing-dashboard" element={<ProtectedRoute requiredRole={USER_ROLES.BILLING}><BillingDashboard /></ProtectedRoute>} />
                      <Route path="/store-dashboard" element={<ProtectedRoute requiredRole={USER_ROLES.STORE}><StoreDashboard /></ProtectedRoute>} />
                      <Route path="/customer-care" element={<ProtectedRoute requiredRole={USER_ROLES.CUSTOMER_CARE}><CustomerCareDashboard /></ProtectedRoute>} />
                      <Route path="/transportation" element={<ProtectedRoute requiredRole={USER_ROLES.LOGISTICS}><TransportationDashboard /></ProtectedRoute>} />
                      <Route path="/receptionist" element={<ProtectedRoute requiredRole={USER_ROLES.RECEPTIONIST}><ReceptionistDashboard /></ProtectedRoute>} />
                      <Route path="/cashier" element={<ProtectedRoute requiredRole={USER_ROLES.CASHIER}><CashierDashboard /></ProtectedRoute>} />
                      <Route path="/users-management" element={<ProtectedRoute requiredRole={USER_ROLES.ADMIN}><UserManagement /></ProtectedRoute>} />
                      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/user-settings" element={<UserSettingsPage />} />
                      <Route path="/settings" element={<ProtectedRoute requiredRole={USER_ROLES.ADMIN}><SettingsPage /></ProtectedRoute>} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
